import { SignedIn, SignedOut, SignIn, useAuth, useClerk } from '@clerk/clerk-react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import { hasPendingPrefill } from './features/leads/wizard/prefill';
import { useEffect, useRef, useState } from 'react';
import { DoorlyLogo } from './components/DoorlyLogo';
import { KeyRound } from 'lucide-react';
import { VENDOR_SIGN_IN_PATH, VendorSignIn, isVendorSignInPath } from './features/auth/VendorSignIn';
import { InvitationTicketGate, hasInvitationTicket } from './features/auth/InvitationTicketGate';
import { clearActingAs } from './lib/actingAs';
import { LogoutPage } from './components/LogoutPage';
import { ProvisionGate } from './components/ProvisionGate';
import { SyncBootstrap } from './components/SyncBootstrap';
import { StuckSessionModal } from './components/StuckSessionModal';
import { Toaster } from './components/Toaster';
import { LegalPage } from './features/legal/LegalPage';
import { routeTree } from './routeTree.gen';

const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  const { signOut, client } = useClerk();
  const { isSignedIn } = useAuth();
  const [stuckSession, setStuckSession] = useState(false);

  // Lien de pré-remplissage reçu (éventuellement avant la connexion, qui
  // ramène à l'accueil) : une fois connecté, on ouvre la création de fiche.
  useEffect(() => {
    if (isSignedIn && hasPendingPrefill() && window.location.pathname !== '/leads/new') {
      void router.navigate({ to: '/leads/new' });
    }
  }, [isSignedIn]);

  useEffect(() => {
    let fired = false;
    const onUnauthorized = () => {
      if (fired) return;
      fired = true;
      void signOut({ redirectUrl: '/' })
        .catch((err) => console.warn('[auth] signOut error:', err))
        .finally(() => {
          // If Clerk still considers us signed in after 500ms, the session
          // is orphaned server-side and signOut cannot clear it. Show modal.
          setTimeout(() => {
            if (window.Clerk?.user) setStuckSession(true);
          }, 500);
        });
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [signOut]);

  // If Clerk is signed in but window.Clerk.session has gone stale, surface the modal.
  useEffect(() => {
    if (!isSignedIn) setStuckSession(false);
  }, [isSignedIn]);

  // Session PIN : l'acteur vit en localStorage, la session Clerk chez Clerk.
  // Si la seconde tombe (401, révocation, expiration) sans passer par
  // `/logout`, le flag survivrait et la PROCHAINE connexion — celle d'un
  // manager sur la même tablette, par exemple — partirait avec `X-Acting-As`
  // et se ferait refuser partout. On l'efface dès que la session n'est plus.
  useEffect(() => {
    if (isSignedIn === false) clearActingAs();
  }, [isSignedIn]);

  // Détecte la transition signed-out → signed-in (= sign-in frais) et
  // marque une fenêtre de 30s pendant laquelle main.tsx s'autorise à
  // auto-reloader si une nouvelle version SW devient disponible.
  // L'utilisateur vient de se connecter et n'est pas encore en saisie,
  // c'est le bon moment pour rafraîchir l'app silencieusement.
  const previousSignedIn = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const prev = previousSignedIn.current;
    previousSignedIn.current = isSignedIn ?? undefined;
    // Ne fire que sur la vraie transition false → true (pas au mount
    // initial avec un user déjà signé in depuis un précédent visite,
    // ni quand isSignedIn passe de undefined à false).
    if (prev === false && isSignedIn === true) {
      (window as unknown as { __doorlyFreshLogin?: number }).__doorlyFreshLogin = Date.now();
      window.dispatchEvent(new CustomEvent('auth:fresh-login'));
    }
  }, [isSignedIn]);

  // Auto-reload sur navigation quand une mise à jour SW est en attente.
  // Le flag `__doorlySwUpdatePending` est posé par main.tsx quand le SW
  // détecte une nouvelle version hors fenêtre post-login. On surveille
  // chaque navigation TanStack Router : dès qu'elle ne va PAS vers le
  // wizard (`/leads/new`), on reload. Couvre les cas :
  //   - User finit son wizard (Terminer) → retour à /leads → reload safe
  //   - User clique sur une fiche → reload safe (la fiche se recharge fresh)
  //   - User va dans Dashboard / Users / Config → reload safe
  // Le wizard reste protégé : entrer dedans = pas de reload, et l'état
  // wizard est persisté en Dexie donc même si on reload après son
  // "Terminer", il ne perd rien.
  useEffect(() => {
    const unsub = router.subscribe('onResolved', (event) => {
      const pending = (window as unknown as { __doorlySwUpdatePending?: boolean })
        .__doorlySwUpdatePending;
      if (!pending) return;
      const nextPath = event.toLocation.pathname;
      if (nextPath === '/leads/new') return;
      window.location.reload();
    });
    return unsub;
  }, []);

  // /logout is handled outside all auth gates — the escape hatch for any stuck state.
  if (window.location.pathname === '/logout') {
    return <LogoutPage />;
  }

  // /legal must work pre- and post-auth (legal compliance: CGU/Mentions Légales
  // accessible to anyone who reaches the URL, including unsigned visitors).
  if (window.location.pathname === '/legal') {
    return <LegalPage />;
  }

  // `/connexion` n'existe que déconnecté : c'est l'écran de connexion des
  // vendeurs sans email, et le routeur — monté seulement dans `SignedIn` — ne
  // déclare pas ce chemin. Quelqu'un de déjà connecté qui y arrive (favori de
  // la tablette, retour arrière, second onglet) tomberait donc sur « Page
  // introuvable » alors que sa session est valide. On le renvoie à l'accueil.
  if (isSignedIn && isVendorSignInPath()) {
    window.location.replace('/');
    return null;
  }

  // Lien d'invitation ouvert avec une session déjà active : sans cette porte,
  // le ticket est ignoré en silence et l'invitation reste en attente.
  if (isSignedIn && hasInvitationTicket()) {
    return (
      <>
        <Toaster />
        <InvitationTicketGate />
      </>
    );
  }

  return (
    <>
      <Toaster />
      <SignedOut>
        {/* Le routeur ne tourne que connecté (il est monté dans `SignedIn`),
            donc la route vendeur se résout ici, sur le pathname brut. C'est
            volontairement rudimentaire : un seul chemin en dur, pas de
            routeur à instancier pour un écran de connexion. */}
        {isVendorSignInPath() ? (
          <VendorSignIn />
        ) : (
        <div className="min-h-dvh grid place-items-center bg-bg p-4">
          <div className="w-full max-w-md space-y-6">
            <header className="flex flex-col items-center gap-3">
              <DoorlyLogo variant="full" size={40} />
              <p className="text-ink-mute text-body">
                Gérez vos fiches terrain en toute simplicité.
              </p>
            </header>
            {/* CSS override en plus de la config Clerk :
                - le footer "Vous n'avez pas encore de compte ? S'inscrire"
                  reste rendu via un sous-élément interne dont la classe
                  varie selon la version Clerk. On force display:none.
                - le mini-logo Doorly à l'intérieur de la SignIn card fait
                  doublon avec notre header externe (logo + tagline). On
                  le masque aussi.

                ATTENTION au périmètre de `.cl-footerAction` : Clerk réutilise
                ce bloc pour DEUX choses. Le renvoi vers l'inscription (qu'on
                masque) et le lien « Utiliser une autre méthode » (qu'il faut
                garder), distingués par le modificateur `__alternativeMethods`.
                Masquer `.cl-footerAction` sans distinction supprimait le seul
                chemin vers la connexion par mot de passe : l'instance affiche
                le code email en premier (`preferred_sign_in_strategy: otp`),
                et un vendeur sans adresse email réelle se retrouvait sans
                aucune issue. D'où les `:not()` ci-dessous.

                Tout le masquage passe par ce bloc et plus par
                `appearance.elements` : les descripteurs Clerk s'appliquent à
                TOUS les éléments d'un type, donc `footerActionLink: 'hidden'`
                réintroduisait exactement le même bug. Un seul mécanisme. */}
            <style>{`
              .cl-footer,
              .cl-footerAction:not(.cl-footerAction__alternativeMethods),
              .cl-footerAction:not(.cl-footerAction__alternativeMethods) .cl-footerActionText,
              .cl-footerAction:not(.cl-footerAction__alternativeMethods) .cl-footerActionLink,
              .cl-internal-1rpdi70,
              .cl-logoBox,
              .cl-logoImage {
                display: none !important;
              }
            `}</style>
            <SignIn
              routing="virtual"
              appearance={{
                elements: {
                  rootBox: 'mx-auto',
                  // Hide the inner Doorly logo — redundant with our
                  // outer DoorlyLogo + tagline above the card.
                  logoBox: 'hidden',
                  logoImage: 'hidden',
                },
              }}
            />
            {/* Sans cette porte, un vendeur qui tape `doorly.pro` — l'URL
                qu'on retient — atterrit ici et n'a aucun moyen de savoir
                qu'une autre page existe. Son identifiant serait pourtant
                accepté par le formulaire ci-dessus, mais il traverserait un
                écran de code envoyé à une adresse qui ne reçoit rien.

                Pas de séparateur « ou » ici : Clerk en place déjà un dans sa
                carte dès qu'une méthode sociale est active, et les deux se
                retrouvaient empilés. L'espacement suffit à séparer l'action
                principale de l'alternative, et ne peut pas se dédoubler si la
                configuration Clerk change. */}
            <div className="space-y-2">
              <a
                href={VENDOR_SIGN_IN_PATH}
                className="flex w-full items-center justify-center gap-2 rounded-[var(--radius-sm)] border border-hair bg-surface px-4 py-2.5 text-body font-semibold text-ink transition-colors hover:bg-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <KeyRound className="h-4 w-4 text-ink-mute" />
                Connexion par identifiant
              </a>
              <p className="text-center text-micro text-ink-mute">
                Pour les vendeurs sans adresse email professionnelle.
              </p>
            </div>
            <footer className="flex items-center justify-center gap-3 text-micro text-ink-mute">
              <a href="/legal?doc=cgu" className="hover:text-ink-soft hover:underline">
                CGU
              </a>
              <span className="opacity-40">·</span>
              <a href="/legal?doc=mentions" className="hover:text-ink-soft hover:underline">
                Mentions légales
              </a>
            </footer>
          </div>
        </div>
        )}
      </SignedOut>
      <SignedIn>
        <ProvisionGate>
          <SyncBootstrap />
          <NuqsAdapter>
            <RouterProvider router={router} />
          </NuqsAdapter>
        </ProvisionGate>
        {stuckSession && <StuckSessionModal client={client} signOut={signOut} />}
      </SignedIn>
    </>
  );
}
